/**
 * クーポン管理画面
 * Phase 11: クーポンの作成・編集・発行管理
 * Created: 2025-09-06
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Calendar,
  DollarSign,
  Percent,
  Gift,
  Tag,
  BarChart,
  TrendingUp,
  Check,
  X,
  AlertCircle,
  Ticket,
} from 'lucide-react';
import axios from 'axios';
import apiClient from '../../utils/apiClient';

import { Lock, Crown } from 'lucide-react';
import PageContainer from '../layout/PageContainer';
// クーポン専用のaxiosインスタンス（apiClientを使用）
const couponAxios = apiClient;

const isUnlocked = true;

interface Coupon {
  id: number;
  code: string;
  name: string; // title as name from backend
  title?: string; // actual DB field
  description: string;
  discount_type: 'fixed' | 'percentage' | 'item'; // actual DB field
  type?: 'amount' | 'percent' | 'item'; // legacy mapping
  discount_value: number; // actual DB field
  value?: number; // legacy mapping
  minimum_purchase: number; // actual DB field
  min_purchase?: number; // legacy mapping
  max_discount?: number;
  target_rank?: string;
  valid_from?: string;
  valid_until?: string;
  valid_days?: number; // actual DB field
  usage_limit: number;
  usage_count?: number;
  active: boolean; // actual DB field
  is_active?: boolean; // legacy mapping
  created_at: string;
  // 統計情報
  total_issued?: number;
  total_used?: number;
  usage_rate?: number;
}

interface CouponStats {
  total_issued: number;
  total_used: number;
  total_expired: number;
  usage_rate: number;
  total_discount_amount: number;
  average_discount: number;
}

interface IssuedCoupon {
  id: number;
  coupon_code: string;
  coupon_name: string;
  member_name: string;
  line_user_id: string;
  status: string;
  issued_date: string;
  used_date: string | null;
  expire_date: string;
  discount_type: string;
  discount_value: number;
}

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [stats, setStats] = useState<CouponStats | null>(null);
  const [activeTab, setActiveTab] = useState<'templates' | 'history'>('templates');
  const [issuedHistory, setIssuedHistory] = useState<IssuedCoupon[]>([]);
  const [historyFilters, setHistoryFilters] = useState({
    templateId: '',
    status: ''
  });
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    target_rank: ''
  });
  const [showBulkIssueResultModal, setShowBulkIssueResultModal] = useState(false);
  const [bulkIssueResults, setBulkIssueResults] = useState<{
    success: number;
    skipped: number;
    failed: number;
    details: { userId: string; success: boolean; skipped: boolean; message: string }[];
  }>({ success: 0, skipped: 0, failed: 0, details: [] });

  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discount_type: 'fixed' as 'percentage' | 'fixed' | 'item',
    discount_value: 0,
    minimum_purchase: 0,
    target_category: '',
    target_menu_id: '',
    valid_days: 30,
    usage_limit: 1,
    auto_send: false,
    send_condition: 'manual',
    send_timing_days: 0,
    active: true
  });

  useEffect(() => {
    if (isUnlocked) fetchCoupons();
  }, [filters]);

  useEffect(() => {
    if (isUnlocked && selectedCoupon) {
      fetchStats(selectedCoupon.id);
    }
  }, [selectedCoupon]);

  useEffect(() => {
    if (isUnlocked && activeTab === 'history') {
      fetchIssuedHistory();
    }
  }, [activeTab, historyFilters]);

  const fetchCoupons = async () => {
    try {
      const response = await couponAxios.get('/coupons/templates', { params: filters });
      const templates = response.data.templates || [];

      // 各クーポンの統計情報を取得
      const couponsWithStats = await Promise.all(
        templates.map(async (coupon: Coupon) => {
          try {
            const statsResponse = await couponAxios.get(`/coupons/${coupon.id}/statistics`);
            const stats = statsResponse.data.data;
            return {
              ...coupon,
              total_issued: stats.total_issued || 0,
              total_used: stats.total_used || 0,
              usage_rate: stats.usage_rate || 0
            };
          } catch (error) {
            // 統計取得失敗時はクーポン情報のみ返す
            return {
              ...coupon,
              total_issued: 0,
              total_used: 0,
              usage_rate: 0
            };
          }
        })
      );

      setCoupons(couponsWithStats);
    } catch (error) {
      console.error('クーポン取得エラー:', error);
      setCoupons([]);
    }
  };

  const fetchStats = async (couponId: number) => {
    try {
      const response = await couponAxios.get(`/coupons/${couponId}/statistics`);
      setStats(response.data.data);
    } catch (error) {
      console.error('統計取得エラー:', error);
    }
  };

  const fetchIssuedHistory = async () => {
    try {
      const params: any = { limit: 100 };
      if (historyFilters.templateId) params.templateId = historyFilters.templateId;
      if (historyFilters.status) params.status = historyFilters.status;

      const response = await couponAxios.get('/coupons/issued', { params });
      setIssuedHistory(response.data.history || []);
    } catch (error) {
      console.error('発行履歴取得エラー:', error);
      setIssuedHistory([]);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await couponAxios.post('/coupons/templates', formData);

      if (response.data.success) {
        alert('クーポンテンプレートを作成しました');
        setShowCreateDialog(false);
        fetchCoupons();
        resetForm();
      }
    } catch (error) {
      console.error('作成エラー:', error);
      alert('クーポンテンプレートの作成に失敗しました');
    }
  };

  const handleUpdate = async () => {
    if (!editingCoupon) return;
    
    try {
      const response = await couponAxios.put(
        `/coupons/${editingCoupon.id}`,
        formData
      );
      
      if (response.data.success) {
        alert('クーポンを更新しました');
        setEditingCoupon(null);
        fetchCoupons();
        resetForm();
      }
    } catch (error) {
      console.error('更新エラー:', error);
      alert('クーポンの更新に失敗しました');
    }
  };

  const handleDelete = async (couponId: number) => {
    if (!confirm('このクーポンを削除しますか？')) return;

    try {
      const response = await couponAxios.delete(`/coupons/${couponId}`);
      if (response.data.success) {
        alert('クーポンを削除しました');
        fetchCoupons();
      }
    } catch (error: any) {
      console.error('クーポン削除エラー:', error);
      // バックエンドからのエラーメッセージを表示
      const errorMessage = error.response?.data?.error || 'クーポンの削除に失敗しました';
      alert(errorMessage);
    }
  };

  const handleBulkIssue = async (couponId: number, skipExistingCheck: boolean = false) => {
    const message = skipExistingCheck
      ? '対象会員全員にクーポンを発行しますか？（既に持っている会員にも発行します）'
      : '対象会員全員にクーポンを発行しますか？（既に持っている会員はスキップします）';

    if (!confirm(message)) return;

    try {
      const response = await couponAxios.post(
        `/coupons/${couponId}/bulk-issue`,
        { skipExistingCheck }
      );

      if (response.data.success) {
        // 結果を保存してモーダルを表示
        setBulkIssueResults({
          success: response.data.successCount || 0,
          skipped: response.data.skippedCount || 0,
          failed: response.data.failedCount || 0,
          details: response.data.results || []
        });
        setShowBulkIssueResultModal(true);
        fetchStats(couponId);
        fetchCoupons();
      }
    } catch (error) {
      console.error('一括発行エラー:', error);
      alert('クーポンの一括発行に失敗しました');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      title: '',
      description: '',
      discount_type: 'fixed',
      discount_value: 0,
      minimum_purchase: 0,
      target_category: '',
      target_menu_id: '',
      valid_days: 30,
      usage_limit: 1,
      auto_send: false,
      send_condition: 'manual',
      send_timing_days: 0,
      active: true
    });
  };

  const startEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      title: coupon.name || coupon.title || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || (coupon.type === 'amount' ? 'fixed' : coupon.type === 'percent' ? 'percentage' : 'item'),
      discount_value: coupon.discount_value ?? coupon.value ?? 0,
      minimum_purchase: coupon.minimum_purchase ?? coupon.min_purchase ?? 0,
      target_category: '',
      target_menu_id: '',
      valid_days: coupon.valid_days || 30,
      usage_limit: coupon.usage_limit || 1,
      auto_send: false,
      send_condition: 'manual',
      send_timing_days: 0,
      active: coupon.active ?? coupon.is_active ?? true
    });
  };

  const getCouponTypeIcon = (type: string) => {
    switch (type) {
      case 'fixed': case 'amount': return <DollarSign className="w-4 h-4" />;
      case 'percentage': case 'percent': return <Percent className="w-4 h-4" />;
      case 'item': return <Gift className="w-4 h-4" />;
      default: return <Tag className="w-4 h-4" />;
    }
  };

  const getCouponTypeLabel = (type: string) => {
    switch (type) {
      case 'fixed': case 'amount': return '金額割引';
      case 'percentage': case 'percent': return '％割引';
      case 'item': return '商品特典';
      default: return type;
    }
  };

  const formatCouponValue = (coupon: Coupon) => {
    const type = coupon.discount_type || coupon.type;
    const value = coupon.discount_value ?? coupon.value ?? 0;

    switch (type as string) {
      case 'fixed': case 'amount': return `${value}円引き`;
      case 'percentage': case 'percent': return `${value}％OFF`;
      case 'item': return '特典付与';
      default: return '-';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">未使用</span>;
      case 'used':
        return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">使用済み</span>;
      case 'expired':
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">期限切れ</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">{status}</span>;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDiscountValue = (type: string, value: number) => {
    if (type === 'percentage') return `${value}%OFF`;
    if (type === 'fixed') return `¥${value}`;
    return '-';
  };

  if (!isUnlocked) {
    return (
      <div className="p-6">
        {/* Premium Overlay */}
        <div style={{
          background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
          borderRadius: '16px',
          padding: '48px 32px',
          color: 'white',
          textAlign: 'center',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(168, 85, 247, 0.3)'
        }}>
          <Crown style={{ width: '64px', height: '64px', marginBottom: '16px', color: '#ffd700' }} className="mx-auto" />
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: 'white' }}>
            クーポン管理
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '32px' }}>
            クーポンテンプレートの作成・編集・一括発行・発行履歴管理ができるプレミアム機能です
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {[
              { icon: <Plus className="w-6 h-6" />, title: 'テンプレート作成', desc: '割引・特典クーポンの定義' },
              { icon: <Users className="w-6 h-6" />, title: '一括発行', desc: '全会員への一括配布' },
              { icon: <BarChart className="w-6 h-6" />, title: '統計・分析', desc: '発行数・使用率・割引額' },
              { icon: <Calendar className="w-6 h-6" />, title: '発行履歴', desc: '会員別の利用状況追跡' },
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ marginBottom: '8px' }} className="flex justify-center">{item.icon}</div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Green Notice */}
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #22c55e',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px'
        }}>
          <p style={{ fontWeight: 'bold', color: '#15803d', marginBottom: '8px', fontSize: '15px' }}>
            📅 検収前初期設定期間
          </p>
          <p style={{ color: '#166534', fontSize: '14px', lineHeight: '1.6' }}>
            <strong>2月25日まで</strong>：検収前初期設定期間につき、クーポン設定の変更は無償にて対応いたします。<br />
            <strong>2月26日以降</strong>：別途お見積りさせていただきます。
          </p>
        </div>

        {/* Blurred Preview */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
          <div style={{ filter: 'blur(8px)', pointerEvents: 'none', opacity: 0.5 }}>
            <div className="p-6 bg-white rounded-lg shadow">
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Ticket className="w-6 h-6" />
                  クーポン管理
                </h2>
              </div>
              <div style={{ height: '200px', background: '#f5f5f5', borderRadius: '8px' }} />
            </div>
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <Lock style={{ width: '48px', height: '48px', color: '#a855f7' }} className="mx-auto" />
            <p style={{ color: '#a855f7', fontWeight: 'bold', marginTop: '8px' }}>プレミアム機能</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Ticket className="w-6 h-6" />
          クーポン管理
        </h2>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          新規作成
        </button>
      </div>

      {/* タブ切り替え */}
      <div className="mb-4 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'templates'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            テンプレート管理
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            発行履歴
          </button>
        </div>
      </div>

      {/* テンプレート管理タブ */}
      {activeTab === 'templates' && (
        <>
          {/* フィルター */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4">
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">すべての種類</option>
                <option value="amount">金額割引</option>
                <option value="percent">％割引</option>
                <option value="item">商品特典</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">すべての状態</option>
                <option value="active">有効</option>
                <option value="inactive">無効</option>
                <option value="expired">期限切れ</option>
              </select>
              <select
                value={filters.target_rank}
                onChange={(e) => setFilters({ ...filters, target_rank: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">すべてのランク</option>
                <option value="bronze">ブロンズ</option>
                <option value="silver">シルバー</option>
                <option value="gold">ゴールド</option>
                <option value="platinum">プラチナ</option>
              </select>
            </div>
          </div>

          {/* クーポンリスト */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {coupons.map(coupon => (
          <div
            key={coupon.id}
            className={`p-4 border rounded-lg cursor-pointer transition-all ${
              selectedCoupon?.id === coupon.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedCoupon(coupon)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                {getCouponTypeIcon(coupon.discount_type || coupon.type)}
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  {getCouponTypeLabel(coupon.discount_type || coupon.type)}
                </span>
              </div>
              {(coupon.active ?? coupon.is_active) ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
            </div>

            <h3 className="font-bold mb-1">{coupon.name}</h3>
            <p className="text-sm text-gray-600 mb-2">{coupon.code}</p>
            <p className="text-sm mb-2">{coupon.description}</p>

            <div className="flex justify-between items-center text-sm">
              <span className="font-bold text-blue-600">
                {formatCouponValue(coupon)}
              </span>
              <span className="text-gray-500">
                {(coupon.usage_count ?? 0)}/{coupon.usage_limit}回使用
              </span>
            </div>

            {((coupon.minimum_purchase ?? coupon.min_purchase ?? 0) > 0) && (
              <p className="text-xs text-gray-500 mt-1">
                最低購入金額: {(coupon.minimum_purchase ?? coupon.min_purchase)?.toLocaleString()}円
              </p>
            )}

            {/* 統計情報表示 */}
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-blue-600">{coupon.total_issued || 0}</p>
                <p className="text-xs text-gray-500">発行数</p>
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{coupon.total_used || 0}</p>
                <p className="text-xs text-gray-500">使用数</p>
              </div>
              <div>
                <p className="text-lg font-bold text-purple-600">{(coupon.usage_rate || 0).toFixed(1)}%</p>
                <p className="text-xs text-gray-500">使用率</p>
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(coupon);
                }}
                className="flex-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs flex items-center justify-center gap-1"
              >
                <Edit className="w-3 h-3" />
                編集
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const choice = confirm(
                    '一括発行方法を選択してください:\n\n' +
                    'OK = スキップモード（既に持っている会員にはスキップ）\n' +
                    'キャンセル = 強制発行モード（全員に発行）'
                  );
                  if (choice === true) {
                    // スキップモード（skipExistingCheck = false）
                    handleBulkIssue(coupon.id, false);
                  } else if (choice === false) {
                    // キャンセルが押された場合、さらに確認
                    const forceConfirm = confirm(
                      '強制発行モードで発行しますか？\n' +
                      '（既に持っている会員にも新しいクーポンを発行します）'
                    );
                    if (forceConfirm) {
                      handleBulkIssue(coupon.id, true);
                    }
                  }
                }}
                className="flex-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs flex items-center justify-center gap-1"
              >
                <Users className="w-3 h-3" />
                一括発行
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(coupon.id);
                }}
                className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs flex items-center justify-center gap-1"
                title="削除"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

          {/* 統計情報 */}
          {selectedCoupon && stats && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <BarChart className="w-4 h-4" />
                {selectedCoupon.name}の統計
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.total_issued || 0}</p>
                  <p className="text-xs text-gray-600">発行数</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.total_used || 0}</p>
                  <p className="text-xs text-gray-600">使用数</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{(stats.usage_rate || 0).toFixed(1)}%</p>
                  <p className="text-xs text-gray-600">使用率</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.total_expired || 0}</p>
                  <p className="text-xs text-gray-600">期限切れ</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">¥{(stats.total_discount_amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-600">総割引額</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">¥{(stats.average_discount || 0).toFixed(0)}</p>
                  <p className="text-xs text-gray-600">平均割引</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 発行履歴タブ */}
      {activeTab === 'history' && (
        <>
          {/* フィルター */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <select
                value={historyFilters.templateId}
                onChange={(e) => setHistoryFilters({ ...historyFilters, templateId: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">すべてのクーポン</option>
                {coupons.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                value={historyFilters.status}
                onChange={(e) => setHistoryFilters({ ...historyFilters, status: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">すべての状態</option>
                <option value="active">未使用</option>
                <option value="used">使用済み</option>
                <option value="expired">期限切れ</option>
              </select>
            </div>
          </div>

          {/* 発行履歴テーブル */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">クーポン名</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">会員名</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">割引内容</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">状態</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">発行日時</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">使用日時</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">有効期限</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {issuedHistory.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        発行履歴がありません
                      </td>
                    </tr>
                  ) : (
                    issuedHistory.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{item.coupon_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <div>{item.member_name || '名前なし'}</div>
                          <div className="text-xs text-gray-500">{item.line_user_id}</div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">
                          {formatDiscountValue(item.discount_type, item.discount_value)}
                        </td>
                        <td className="px-4 py-3 text-sm">{getStatusBadge(item.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.issued_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.used_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.expire_date)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 統計サマリー */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold">{issuedHistory.length}</p>
                <p className="text-xs text-gray-600">総発行数</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{issuedHistory.filter(h => h.status === 'active').length}</p>
                <p className="text-xs text-gray-600">未使用</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{issuedHistory.filter(h => h.status === 'used').length}</p>
                <p className="text-xs text-gray-600">使用済み</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{issuedHistory.filter(h => h.status === 'expired').length}</p>
                <p className="text-xs text-gray-600">期限切れ</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 作成・編集ダイアログ */}
      {(showCreateDialog || editingCoupon) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">
              {editingCoupon ? 'クーポン編集' : 'クーポン作成'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">クーポンコード</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: SUMMER2025"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">クーポン名</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: 夏の特別割引"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">説明</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="例: 期間限定の特別クーポンです"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">クーポン種別</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed' | 'item' })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fixed">金額割引</option>
                  <option value="percentage">パーセント割引</option>
                  <option value="item">商品特典</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {formData.discount_type === 'fixed' ? '割引額（円）' : formData.discount_type === 'percentage' ? '割引率（％）' : '特典内容'}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const step = formData.discount_type === 'fixed' ? 50 : 5;
                      setFormData({ ...formData, discount_value: Math.max(0, formData.discount_value - step) });
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={formData.discount_value || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormData({ ...formData, discount_value: val });
                    }}
                    step={formData.discount_type === 'fixed' ? 50 : 5}
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder={formData.discount_type === 'fixed' ? '例: 100' : '例: 10'}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const step = formData.discount_type === 'fixed' ? 50 : 5;
                      setFormData({ ...formData, discount_value: formData.discount_value + step });
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">最低購入金額（円）</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, minimum_purchase: Math.max(0, formData.minimum_purchase - 100) })}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={formData.minimum_purchase || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormData({ ...formData, minimum_purchase: val });
                    }}
                    step="100"
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="例: 1000"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, minimum_purchase: formData.minimum_purchase + 100 })}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">有効日数</label>
                <input
                  type="number"
                  value={formData.valid_days}
                  onChange={(e) => setFormData({ ...formData, valid_days: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: 30（発行から30日間有効）"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">使用回数制限（1人あたり）</label>
                <input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: 1（1人1回まで）"
                />
              </div>
            </div>
            
            <div className="mt-6 flex gap-2">
              <button
                onClick={() => {
                  if (editingCoupon) {
                    handleUpdate();
                  } else {
                    handleCreate();
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingCoupon ? '更新' : '作成'}
              </button>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setEditingCoupon(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 一括発行結果モーダル */}
      {showBulkIssueResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 flex justify-between items-center bg-gradient-to-r from-green-500 to-green-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                一括発行結果
              </h2>
              <button
                onClick={() => setShowBulkIssueResultModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {/* サマリー */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{bulkIssueResults.success}</p>
                    <p className="text-sm text-gray-600 mt-1">✅ 発行成功</p>
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-600">{bulkIssueResults.skipped}</p>
                    <p className="text-sm text-gray-600 mt-1">⏭️ スキップ</p>
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600">{bulkIssueResults.failed}</p>
                    <p className="text-sm text-gray-600 mt-1">❌ 発行失敗</p>
                  </div>
                </div>
              </div>

              {/* 詳細メッセージ */}
              <div className="space-y-4">
                {bulkIssueResults.success > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-semibold text-green-700 mb-2">
                      ✅ {bulkIssueResults.success}名にクーポンを発行しました
                    </p>
                  </div>
                )}

                {bulkIssueResults.skipped > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm font-semibold text-yellow-700 mb-2">
                      ⏭️ {bulkIssueResults.skipped}名はスキップされました（既にクーポン所持）
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      💡 ヒント: 既に持っている会員にも発行したい場合は、「強制発行モード」を選択してください。
                    </p>
                  </div>
                )}

                {bulkIssueResults.failed > 0 && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-semibold text-red-700 mb-2">
                      ❌ {bulkIssueResults.failed}名は発行に失敗しました
                    </p>
                  </div>
                )}

                {/* 詳細リスト（上位10件のみ表示） */}
                {bulkIssueResults.details.length > 0 && bulkIssueResults.details.slice(0, 10).length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">詳細（上位10件）:</p>
                    <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                      {bulkIssueResults.details.slice(0, 10).map((detail, index) => (
                        <div key={index} className="flex items-center gap-2">
                          {detail.success ? (
                            <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                          ) : detail.skipped ? (
                            <AlertCircle className="w-3 h-3 text-yellow-600 flex-shrink-0" />
                          ) : (
                            <X className="w-3 h-3 text-red-600 flex-shrink-0" />
                          )}
                          <span className="text-gray-600">{detail.userId}</span>
                          <span className="text-gray-500">- {detail.message}</span>
                        </div>
                      ))}
                      {bulkIssueResults.details.length > 10 && (
                        <p className="text-gray-500 mt-2">...他 {bulkIssueResults.details.length - 10}件</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowBulkIssueResultModal(false)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;